import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import {
  ALL_PROVIDER_OPTIONS,
  TransactionProviders,
  type IResolvedTransactionOption,
} from '@principle-theorem/ng-payments';
import { InformationBoxComponent } from '@principle-theorem/ng-principle-shared';
import {
  ButtonsContainerComponent,
  NgCommonModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { ManualTransactionType } from '@principle-theorem/principle-core';
import {
  TransactionProvider,
  type IAvailablePaymentProvider,
  type IBrand,
  type IBrandPaymentMethodSettings,
  type IManualTransactionType,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  multiFilter,
  patchDoc,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, groupBy } from 'lodash';
import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

interface IBrandPaymentMethodSettingsForm {
  insuranceFundsAccepted: string[];
  governmentPrograms: string[];
  paymentPlanDescriptions: string;
}

interface IPaymentProviderRef {
  providerId: TransactionProvider;
  transactionTypeRef?: DocumentReference<IManualTransactionType>;
}

interface IPaymentProviderEntry extends IPaymentProviderRef {
  label: string;
}

// A row groups every provider sharing one label: HICAPS and Medipass each
// register a separate provider per capture flow (EFTPOS, health fund, Medicare,
// DVA) under the same name, which patients see as a single payment method.
interface IPaymentProviderRow {
  key: string;
  label: string;
  providers: IPaymentProviderRef[];
  isManual: boolean;
  isSelected: boolean;
  patientFriendlyName: string;
}

@Component({
  selector: 'pr-payment-method-availability',
  templateUrl: './payment-method-availability.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatListModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    InformationBoxComponent,
    ButtonsContainerComponent,
  ],
})
export class PaymentMethodAvailabilityComponent {
  private readonly _transactionProviders = inject(TransactionProviders);
  private _providerRows$ = new BehaviorSubject<IPaymentProviderRow[]>([]);
  readonly providerRows$ = this._providerRows$.asObservable();
  readonly separatorKeyCodes = [ENTER, COMMA] as const;
  brand$ = new ReplaySubject<WithRef<IBrand>>(1);

  form = new TypedFormGroup<IBrandPaymentMethodSettingsForm>({
    insuranceFundsAccepted: new TypedFormControl<string[]>([]),
    governmentPrograms: new TypedFormControl<string[]>([]),
    paymentPlanDescriptions: new TypedFormControl<string>(''),
  });

  @Input()
  set brand(brand: WithRef<IBrand>) {
    if (brand) {
      this.brand$.next(brand);
      this._setFormValues(brand);
    }
  }

  constructor() {
    const manualTransactionTypes$ = this.brand$.pipe(
      switchMap((brand) => ManualTransactionType.all$(brand, true)),
      multiFilter((type) => !type.deleted)
    );

    combineLatest([
      this._transactionProviders.resolveProviders$(ALL_PROVIDER_OPTIONS),
      manualTransactionTypes$,
      this.brand$,
    ])
      .pipe(
        map(([resolvedProviders, manualTypes, brand]) =>
          this._buildProviderRows(
            resolvedProviders,
            manualTypes,
            brand.settings.paymentMethods
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe((rows) => this._providerRows$.next(rows));
  }

  addInsuranceFund(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      const current = this.form.controls.insuranceFundsAccepted.value ?? [];
      this.form.controls.insuranceFundsAccepted.setValue([...current, value]);
    }
    event.chipInput.clear();
  }

  removeInsuranceFund(item: string): void {
    const current = this.form.controls.insuranceFundsAccepted.value ?? [];
    this.form.controls.insuranceFundsAccepted.setValue(
      current.filter((entry) => entry !== item)
    );
  }

  addGovernmentProgram(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      const current = this.form.controls.governmentPrograms.value ?? [];
      this.form.controls.governmentPrograms.setValue([...current, value]);
    }
    event.chipInput.clear();
  }

  removeGovernmentProgram(item: string): void {
    const current = this.form.controls.governmentPrograms.value ?? [];
    this.form.controls.governmentPrograms.setValue(
      current.filter((entry) => entry !== item)
    );
  }

  toggleProvider(row: IPaymentProviderRow, isSelected: boolean): void {
    this._updateProviderRow(row.key, { isSelected });
  }

  updatePatientFriendlyName(row: IPaymentProviderRow, event: Event): void {
    const input = event.target as HTMLInputElement;
    this._updateProviderRow(row.key, { patientFriendlyName: input.value });
  }

  async save(): Promise<void> {
    const brand = await snapshot(this.brand$);
    const providerRows = this._providerRows$.value;
    const formValue = this.form.getRawValue();

    const availableProviders = providerRows
      .filter((row) => row.isSelected)
      .flatMap((row) => {
        const patientFriendlyName = row.patientFriendlyName.trim() || row.label;
        return row.providers.map((provider) => ({
          provider: provider.providerId,
          transactionTypeRef: provider.transactionTypeRef,
          patientFriendlyName,
        }));
      })
      .filter((row) => row.patientFriendlyName);

    const insuranceFundsAccepted = (formValue.insuranceFundsAccepted ?? [])
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
    const governmentPrograms = (formValue.governmentPrograms ?? [])
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
    const paymentPlanDescriptions =
      formValue.paymentPlanDescriptions.trim() || undefined;

    const paymentMethods: IBrandPaymentMethodSettings = {
      availableProviders: availableProviders.length
        ? availableProviders
        : undefined,
      insuranceFundsAccepted: insuranceFundsAccepted.length
        ? insuranceFundsAccepted
        : undefined,
      governmentPrograms: governmentPrograms.length
        ? governmentPrograms
        : undefined,
      paymentPlanDescriptions,
    };

    await patchDoc(brand.ref, {
      settings: {
        ...brand.settings,
        paymentMethods,
      },
    });
  }

  private _setFormValues(brand: WithRef<IBrand>): void {
    const settings = brand.settings.paymentMethods;
    this.form.patchValue(
      {
        insuranceFundsAccepted: [...(settings?.insuranceFundsAccepted ?? [])],
        governmentPrograms: [...(settings?.governmentPrograms ?? [])],
        paymentPlanDescriptions: settings?.paymentPlanDescriptions ?? '',
      },
      { emitEvent: false }
    );
  }

  private _updateProviderRow(
    key: string,
    changes: Partial<IPaymentProviderRow>
  ): void {
    const rows = this._providerRows$.value.map((row) =>
      row.key === key ? { ...row, ...changes } : row
    );
    this._providerRows$.next(rows);
  }

  private _buildProviderRows(
    resolvedProviders: IResolvedTransactionOption[],
    manualTypes: WithRef<IManualTransactionType>[],
    settings?: IBrandPaymentMethodSettings
  ): IPaymentProviderRow[] {
    const availableProviderMap = this._buildAvailableProviderMap(
      settings?.availableProviders
    );

    const entries: IPaymentProviderEntry[] = [
      ...resolvedProviders.map((option) => ({
        providerId: option.provider.providerId,
        label: option.label,
      })),
      ...manualTypes.map((type) => ({
        providerId: TransactionProvider.Manual,
        label: type.name,
        transactionTypeRef: type.ref,
      })),
    ];

    return Object.values(groupBy(entries, (entry) => this._rowKey(entry)))
      .map((groupedEntries) =>
        this._buildProviderRow(groupedEntries, availableProviderMap)
      )
      .sort((a, b) => {
        if (a.isManual !== b.isManual) {
          return a.isManual ? 1 : -1;
        }
        return a.label.localeCompare(b.label);
      });
  }

  private _buildProviderRow(
    entries: IPaymentProviderEntry[],
    availableProviderMap: Map<string, IAvailablePaymentProvider>
  ): IPaymentProviderRow {
    const [firstEntry] = entries;
    const saved = compact(
      entries.map((entry) =>
        availableProviderMap.get(
          this._providerKey(entry.providerId, entry.transactionTypeRef)
        )
      )
    );

    return {
      key: this._rowKey(firstEntry),
      label: firstEntry.label,
      providers: entries.map(({ providerId, transactionTypeRef }) => ({
        providerId,
        transactionTypeRef,
      })),
      isManual: this._isManual(firstEntry.providerId),
      isSelected: saved.length > 0,
      patientFriendlyName: saved[0]?.patientFriendlyName ?? firstEntry.label,
    };
  }

  private _rowKey(entry: IPaymentProviderEntry): string {
    // Manual transaction types are keyed by their unique ref so distinct types
    // that happen to share a name stay in separate rows. Resolved providers are
    // keyed by label so multiple registrations of the same provider (e.g. HICAPS
    // across capture flows) intentionally merge into one row.
    if (this._isManual(entry.providerId)) {
      return `manual:${entry.transactionTypeRef?.path ?? entry.label}`;
    }
    return `provider:${entry.label}`;
  }

  private _isManual(providerId: TransactionProvider): boolean {
    return providerId === TransactionProvider.Manual;
  }

  private _buildAvailableProviderMap(
    availableProviders: IAvailablePaymentProvider[] = []
  ): Map<string, IAvailablePaymentProvider> {
    const providerMap = new Map<string, IAvailablePaymentProvider>();
    availableProviders.forEach((provider) => {
      providerMap.set(
        this._providerKey(provider.provider, provider.transactionTypeRef),
        provider
      );
    });
    return providerMap;
  }

  private _providerKey(
    providerId: TransactionProvider,
    transactionTypeRef?: DocumentReference<IManualTransactionType>
  ): string {
    if (providerId === TransactionProvider.Manual && transactionTypeRef) {
      return `${providerId}:${transactionTypeRef.path}`;
    }
    return String(providerId);
  }
}
